import { Link } from 'react-router-dom';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { cn } from '@/lib/utils';

interface ProbeCardProps {
  id: number;
  name: string;
  enabled: boolean;
  totalChecks: number;
  successfulChecks: number;
  averageResponseTime: number | null;
  lastCheckedAt: string | null;
  isOverdue: boolean;
}

function formatLastChecked(lastCheckedAt: string | null): string {
  if (lastCheckedAt === null) {
    return 'Never';
  }

  const date = new Date(lastCheckedAt);
  if (Number.isNaN(date.getTime())) {
    return 'Unavailable';
  }

  return new Intl.DateTimeFormat(undefined, {
    dateStyle: 'medium',
    timeStyle: 'short',
  }).format(date);
}

export function ProbeCard({
  id,
  name,
  enabled,
  totalChecks,
  successfulChecks,
  averageResponseTime,
  lastCheckedAt,
  isOverdue,
}: ProbeCardProps) {
  const hasRecentData = totalChecks > 0;
  const hasMeasurementHistory = lastCheckedAt !== null;
  const successRate = hasRecentData ? Math.round((successfulChecks / totalChecks) * 100) : null;
  const lastCheckedLabel = formatLastChecked(lastCheckedAt);

  return (
    <Card
      className={cn('transition-all hover:shadow-md', !enabled && 'border-gray-300 bg-gray-50')}
    >
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between gap-3">
          <CardTitle className={cn('min-w-0 text-lg', !enabled && 'text-gray-500')}>
            <Link
              to={`/dashboard/monitors/${id}/results`}
              className="hover:text-primary hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
            >
              {name}
            </Link>
          </CardTitle>
          {!enabled && (
            <Badge variant="outline" className="shrink-0 bg-gray-200 text-gray-600">
              Disabled
            </Badge>
          )}
          {isOverdue && <Badge variant="outline">Check overdue</Badge>}
        </div>
      </CardHeader>
      <CardContent>
        {hasRecentData ? (
          <div className="space-y-2">
            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-600">Success Rate</span>
              <span
                className={cn(
                  'text-sm font-semibold',
                  !enabled && 'text-gray-500',
                  enabled && successRate !== null && successRate >= 95 && 'text-green-600',
                  enabled &&
                    successRate !== null &&
                    successRate < 95 &&
                    successRate >= 80 &&
                    'text-yellow-600',
                  enabled && successRate !== null && successRate < 80 && 'text-red-600',
                )}
              >
                {successRate}%
              </span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-600">Avg Response</span>
              <span className={cn('text-sm font-semibold', !enabled && 'text-gray-500')}>
                {averageResponseTime !== null ? `${averageResponseTime}ms` : '—'}
              </span>
            </div>
          </div>
        ) : (
          <div className="py-2 text-center text-sm text-gray-500">
            {hasMeasurementHistory ? 'No measurements in the last hour' : 'No measurements yet'}
          </div>
        )}
        <div className="mt-4 flex items-center justify-between gap-3 border-t pt-3 text-sm">
          <span className="text-gray-600">Last checked</span>
          <time
            dateTime={lastCheckedAt ?? undefined}
            className={cn('text-right font-medium text-gray-900', !enabled && 'text-gray-500')}
          >
            {lastCheckedLabel}
          </time>
        </div>
      </CardContent>
      <CardFooter>
        <Link
          to={`/dashboard/monitors/${id}/results`}
          className="text-sm font-medium text-primary underline-offset-4 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
        >
          View probe results
        </Link>
      </CardFooter>
    </Card>
  );
}
